import React, { useState, useEffect } from 'react';
import {
  Container,
  Card,
  CardContent,
  Typography,
  Button,
  Alert,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogContent,
  DialogTitle,
  CircularProgress
} from '@mui/material';
import {
  Phone as PhoneIcon,
  AccessTime as AccessTimeIcon,
  Person as PersonIcon,
  Info as InfoIcon,
  PlayArrow as PlayIcon,
  Refresh as RefreshIcon,
  Add as AddIcon,
  Timeline as ActivityIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { callService } from '../services/api';
import { useAuth } from '../utils/AuthContext';
import AudioPlayer from '../components/AudioPlayer';

const CallsPage = () => {
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const [wsConnected, setWsConnected] = useState(false);
  const [audioDialogOpen, setAudioDialogOpen] = useState(false);
  const [selectedRecording, setSelectedRecording] = useState(null);
  const { currentUser } = useAuth();

  const fetchCalls = async () => {
    try {
      console.log('Fetching calls...');
      const data = await callService.getCalls();
      console.log('Fetched calls data:', data);
      
      // Handle both data structures: direct array or {calls: array, pagination: object}
      const callsArray = data.calls || data;
      console.log('Processed calls array:', callsArray);
      
      if (!Array.isArray(callsArray)) {
        console.error('Invalid calls data structure:', data);
        setError('Invalid data format received from server');
        setLoading(false);
        return;
      }
      
      setCalls(callsArray);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching calls:', error);
      setError(`Failed to load calls: ${error.message || 'Unknown error'}`);
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    setError('');
    await fetchCalls();
    setRefreshing(false);
  };

  useEffect(() => {
    fetchCalls();
  }, []);

  // WebSocket connection for real-time updates
  useEffect(() => {
    if (!currentUser) return;

    const token = localStorage.getItem('token');
    if (!token) return;

    // Connect to WebSocket for real-time updates
    const wsUrl = `${process.env.REACT_APP_WS_URL || window.location.origin.replace('http', 'ws')}/ws?token=${token}`;
    let ws = null;

    try {
      ws = new WebSocket(wsUrl);

      ws.onopen = () => {
        console.log('WebSocket connected for call updates');
        setWsConnected(true);
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          
          if (data.type === 'call:status_update') {
            console.log('Received call status update:', data.callUpdate);
            
            // Update the call in the list with all new data
            setCalls(prevCalls => {
              return prevCalls.map(call => {
                if (call._id === data.callUpdate.callId || call.id === data.callUpdate.callId) {
                  return {
                    ...call,
                    status: data.callUpdate.status,
                    duration: data.callUpdate.duration,
                    startTime: data.callUpdate.startTime || call.startTime,
                    endTime: data.callUpdate.endTime || call.endTime,
                    cost: data.callUpdate.cost || call.cost,
                    recordingUrl: data.callUpdate.recordingUrl || call.recordingUrl,
                    transcript: data.callUpdate.transcript || call.transcript,
                    ...(data.callUpdate.agentName && { agentId: { name: data.callUpdate.agentName } }),
                    updatedAt: new Date()
                  };
                }
                return call;
              });
            });
          }
        } catch (error) {
          console.error('Error parsing WebSocket message:', error);
        }
      };

      ws.onerror = (error) => {
        console.warn('WebSocket connection failed - real-time updates disabled. This is normal if the WebSocket server is not running.');
        setWsConnected(false);
      };

      ws.onclose = () => {
        console.log('WebSocket disconnected');
        setWsConnected(false);
      };

    } catch (error) {
      console.warn('Failed to create WebSocket connection - real-time updates disabled:', error);
      setWsConnected(false);
    }

    // Cleanup on unmount
    return () => {
      if (ws && ws.readyState === WebSocket.OPEN) {
        ws.close();
      }
    };
  }, [currentUser]);

  // Periodic refresh for call status updates (every 30 seconds)
  useEffect(() => {
    if (!currentUser || calls.length === 0) return;

    const interval = setInterval(() => {
      // Only refresh if there are active calls (not completed/failed)
      const hasActiveCalls = calls.some(call => 
        call.status === 'initiated' || 
        call.status === 'ringing' || 
        call.status === 'answered'
      );
      
      if (hasActiveCalls) {
        console.log('Auto-refreshing call statuses...');
        fetchCalls();
      }
    }, 30000); // 30 seconds

    return () => clearInterval(interval);
  }, [currentUser, calls]);

  // Helper function to get phone numbers from call data
  const getPhoneNumbers = (call) => {
    // Check if we have metadata with fromNumber/toNumber
    if (call.metadata && call.metadata.fromNumber && call.metadata.toNumber) {
      return {
        fromNumber: call.metadata.fromNumber,
        toNumber: call.metadata.toNumber
      };
    }
    
    // Fallback to phoneNumber field
    return {
      fromNumber: call.phoneNumber || 'N/A',
      toNumber: call.phoneNumber || 'N/A'
    };
  };

  // Helper function to get agent name
  const getAgentName = (call) => {
    if (call.agentId && typeof call.agentId === 'object' && call.agentId.name) {
      return call.agentId.name;
    }
    if (call.agentName) {
      return call.agentName;
    }
    return 'Unknown Agent';
  };

  // Helper function to get agent ID
  const getAgentId = (call) => {
    if (call.agentId && typeof call.agentId === 'object' && call.agentId._id) {
      return call.agentId._id;
    }
    if (call.agentId && typeof call.agentId === 'string') {
      return call.agentId;
    }
    return null;
  };

  // Helper function to format duration
  const formatDuration = (seconds) => {
    if (!seconds) return 'N/A';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Helper function to format cost
  const formatCost = (cost) => {
    if (!cost) return 'N/A';
    return `$${parseFloat(cost).toFixed(4)}`;
  };

  // Helper function to get call timing
  const getCallTiming = (call) => {
    if (!call.startTime) return 'N/A';
    
    const startTime = new Date(call.startTime);
    const endTime = call.endTime ? new Date(call.endTime) : null;
    
    const formatTime = (date) => {
      return date.toLocaleTimeString('en-US', { 
        hour: '2-digit', 
        minute: '2-digit',
        hour12: true 
      });
    };
    
    const startTimeStr = formatTime(startTime);
    const endTimeStr = endTime ? formatTime(endTime) : 'Ongoing';
    
    return `${startTimeStr} - ${endTimeStr}`;
  };

  // Helper function to get status badge color
  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'failed':
        return 'error';
      case 'answered':
        return 'info';
      case 'ringing':
        return 'warning';
      case 'initiated':
        return 'primary';
      default:
        return 'default';
    }
  };

  const handlePlayRecording = (recordingUrl) => {
    if (recordingUrl) {
      setSelectedRecording(recordingUrl);
      setAudioDialogOpen(true);
    }
  };

  const handleCloseAudioDialog = () => {
    setAudioDialogOpen(false);
    setSelectedRecording(null);
  };

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1" gutterBottom>
          Call History
        </Typography>
        <Box display="flex" gap={2}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
            disabled={refreshing}
          >
            {refreshing ? 'Refreshing...' : 'Refresh'}
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            component={Link}
            to="/calls/create"
          >
            New Call
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {wsConnected && (
        <Alert severity="success" sx={{ mb: 3 }}>
          <ActivityIcon sx={{ mr: 1 }} />
          Real-time updates connected
        </Alert>
      )}

      {!wsConnected && calls.length > 0 && (
        <Alert severity="info" sx={{ mb: 3 }}>
          <ActivityIcon sx={{ mr: 1 }} />
          Real-time updates disabled - call status will refresh every 30 seconds
        </Alert>
      )}

      {calls.length === 0 ? (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 8 }}>
            <PhoneIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No calls found
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Start making calls to see them appear here
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              component={Link}
              to="/calls/create"
            >
              Make Your First Call
            </Button>
          </CardContent>
        </Card>
      ) : (
        <TableContainer component={Paper} elevation={2}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Status</TableCell>
                <TableCell>Agent</TableCell>
                <TableCell>Phone Numbers</TableCell>
                <TableCell>Duration</TableCell>
                <TableCell>Cost</TableCell>
                <TableCell>Timing</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {calls.map((call) => {
                const phoneNumbers = getPhoneNumbers(call);
                const agentName = getAgentName(call);
                const agentId = getAgentId(call);
                
                return (
                  <TableRow key={call._id || call.id}>
                    <TableCell>
                      <Chip
                        label={call.status}
                        color={getStatusColor(call.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <PersonIcon fontSize="small" />
                        <Typography variant="body2">
                          {agentId ? (
                            <Link to={`/agents/${agentId}`} style={{ textDecoration: 'none' }}>
                              {agentName}
                            </Link>
                          ) : (
                            agentName
                          )}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          From: {phoneNumbers.fromNumber}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          To: {phoneNumbers.toNumber}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <AccessTimeIcon fontSize="small" />
                        <Typography variant="body2">
                          {formatDuration(call.duration)}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatCost(call.cost)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {getCallTiming(call)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" gap={1}>
                        <Tooltip title="View Details">
                          <IconButton
                            size="small"
                            component={Link}
                            to={`/calls/${call._id || call.id}`}
                          >
                            <InfoIcon />
                          </IconButton>
                        </Tooltip>
                        {call.recordingUrl && (
                          <Tooltip title="Play Recording">
                            <IconButton
                              size="small"
                              onClick={() => handlePlayRecording(call.recordingUrl)}
                            >
                              <PlayIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Audio Player Dialog */}
      <Dialog
        open={audioDialogOpen}
        onClose={handleCloseAudioDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Call Recording</Typography>
            <IconButton onClick={handleCloseAudioDialog}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedRecording && (
            <AudioPlayer
              audioUrl={selectedRecording}
              title="Call Recording"
              height={80}
              showTitle={false}
            />
          )}
        </DialogContent>
      </Dialog>
    </Container>
  );
};

export default CallsPage;
